import React from 'react'

import SEO from 'src/components/seo'
import { withTranslation } from 'gatsby-plugin-react-i18next'
import { graphql } from 'gatsby'

import {
  Container,
  StyledGatsbyLink,
  OverflowContainer,
  Text,
  PageTitle,
  SimpleListItem,
  PrivacyOrderedList,
  PrivacyUnorderedList,
  ALink,
} from '../templates/ShopifyPage/styles'
import TosNav from '../components/TosNav'

const SmsTerms = () => (
  <>
    <SEO
      title="SMS Terms of Service"
      description="The Skylight Frame (Glimpse LLC) legal SMS Terms of Service."
      path="/sms-terms/"
    />
    <Container>
      <TosNav currentRoutePath="/sms-terms/" />
      <OverflowContainer>
        <Text>Last updated 10/21/2025</Text>
        <PageTitle>Skylight Frame Messaging Terms &amp; Conditions</PageTitle>
        <br />
        <Text>
          Skylight Frame (hereinafter, "Skylight Frame," "We," "Us," "Our") is offering a mobile
          messaging program (the "Program"), which you agree to use and participate in subject to
          these Mobile Messaging Terms and Conditions and{' '}
          <StyledGatsbyLink to="/sms-privacy">Messaging Privacy Policy</StyledGatsbyLink>.
        </Text>
        <Text>
          By opting in to or participating in any of our Programs, you accept and agree to these
          terms and conditions, including, without limitation, your agreement to resolve any
          disputes with us through binding, individual-only arbitration, as detailed in the "Dispute
          Resolution" section below. In arbitration there is less discovery and appellate review
          than in court. Please review carefully.
        </Text>
        <Text>
          By opting in to or participating in any of our Programs, you represent and warrant that
          you do so because of a genuine desire to receive discounts, promotions, and other news or
          information from Us and not because of a desire or intent to initiate litigation or bring
          legal claims against any party. If you have requested to receive messages with a desire to
          bring legal claims against Us or any vendors acting on our behalf, then you shall
          immediately unsubscribe using the instructions set forth below and agree to indemnify Us
          and/or those vendors for any expenses, including attorneys' fees, associated with
          defending such claims.
        </Text>
        <Text>
          This Agreement is limited to the Program and is not intended to modify other Terms and
          Conditions or Privacy Policy that may govern the relationship between you and Us in other
          contexts (including those in the "Other Terms and Policies" section below). Regardless of
          the opt-in method you utilized to join the Program, you agree that this Agreement applies
          to your participation in the Program.
        </Text>
        <Text>
          <b>1. User Opt In.</b>
        </Text>
        <PrivacyOrderedList>
          <SimpleListItem>
            <Text>
              <b>Consent to Receive Recurring Automated Messages.</b> You agree to receive recurring
              automated promotional and personalized marketing text (e.g., SMS, MMS, and RCS)
              messages (including cart reminders) from Skylight Frame, including messages that may
              be sent using an automatic telephone dialing system, to the mobile telephone number
              you provided when signing up or any other number that you designate. You further
              consent to receiving messages that include artificial or prerecorded voices. By
              providing your phone number, you represent and warrant that you are providing your own
              phone number, and not someone else's, and that you are a legally-authorized user of
              this phone number. Consent to receive automated marketing messages is not a condition
              of any purchase. While you consent to receive messages sent using an autodialer, the
              foregoing shall not be interpreted to suggest or imply that any or all of Our mobile
              messages are sent using an automatic telephone dialing system ("ATDS" or
              "autodialer"). Message and data rates may apply. Message frequency varies.
            </Text>
          </SimpleListItem>
          <SimpleListItem>
            <Text>
              <b>No Time of Day Restrictions.</b> While efforts are made to limit the delivery of
              messages outside of reasonable hours for most consumers, you understand and agree that
              We do not have the ability to target messages based on geographic location because
              federal law and privacy concerns prevent a consumer's real-time location data from
              being shared with Us or our vendors by your wireless carrier. Further, technical
              issues, such as network congestion or your phone being disconnected from your wireless
              service, can result in messages being delivered at unexpected times. Therefore, you
              agree that your consent to receive marketing text messages includes consent to the
              delivery of such messages 24-hours per day. Your consent supersedes any state or
              federal regulation that might otherwise restrict the delivery of such messages and you
              waive any such claims.
            </Text>
          </SimpleListItem>
          <SimpleListItem>
            <Text>
              <b>Consent to Receipt of Electronic Information and E-Sign.</b> Notwithstanding
              anything herein to the contrary, you understand that applicable law may require Us to
              provide certain information to you in writing in connection with any consent to
              receive advertising and telemarketing messages. By opting-in to Our messaging program,
              you authorize Us to provide this information to you electronically instead of in a
              separate paper document. You understand that you may withdraw this consent, update
              your information, or request a free paper copy of the information by emailing us at{' '}
              <ALink href="mailto:help@myskylight.com">help@myskylight.com</ALink>. You understand
              that to access and retain a copy of this webpage, you will need: (i) a device (such as
              a computer or mobile phone) with a web browser and Internet access; and (ii) available
              storage space on that device to download a copy of this webpage of a connected printer
              to print a copy of this webpage.
            </Text>
          </SimpleListItem>
          <SimpleListItem>
            <Text>
              <b>
                Disclosure of Data and Receipt of One Time Messages to Identify Device and Prevent
                Fraud.
              </b>{' '}
              You authorize the use or disclosure of information about your account and your
              wireless device, if available, by or to your wireless carrier, Skylight Frame or Our
              service provider(s), including Stodge Inc. dba Postscript, to help identify you or
              your wireless device when you opt-in to receive text messages from any Postscript
              customer and to endeavor to detect and prevent fraud. Further, you consent to the
              transmission of one-time passcodes or other messages to confirm your intent to
              subscribe to our recurring message program. See our{' '}
              <StyledGatsbyLink to="/sms-privacy">Messaging Privacy Policy</StyledGatsbyLink> for
              how we treat your data.
            </Text>
          </SimpleListItem>
        </PrivacyOrderedList>
        <Text>
          <b>2. User Opt Out.</b> If you do not wish to continue participating in the Program or no
          longer agree to this Agreement, you agree to reply STOP, END, CANCEL, REVOKE, OPT OUT,
          UNSUBSCRIBE, or QUIT to any mobile message from Us in order to opt out of the Program. You
          may receive an additional mobile message confirming your decision to opt out. To the
          maximum extent permitted by law, You understand and agree that the foregoing options are
          the only reasonable methods of opting out. You acknowledge that our text message platform
          may not recognize and respond to unsubscribe requests that alter, change, or modify the
          STOP, END, CANCEL, REVOKE, OPT OUT, UNSUBSCRIBE or QUIT keyword commands, such as the use
          of different spellings or the addition of other words or phrases to the command, and agree
          that Skylight Frame and its service providers will not have liability for failing to honor
          requests that are designed to circumvent the automated opt-out processes described above.
          You agree that other methods of opting out, including, but not limited to, texting words
          other than those set forth above or verbally requesting one of our employees to remove you
          from our list, may not be deemed to be reasonable methods of opting out. Our Do Not Call
          Policy is attached to these Terms of Service as Appendix A.
        </Text>
        <Text>
          <b>3. Other Terms and Policies.</b> You also agree to our{' '}
          <StyledGatsbyLink to="/sms-privacy">Messaging Privacy Policy</StyledGatsbyLink>,{' '}
          <StyledGatsbyLink to="/tos">Skylight Frame Terms of Service</StyledGatsbyLink> and{' '}
          <StyledGatsbyLink to="/privacy-policy">Skylight Frame Privacy Policy</StyledGatsbyLink>.
        </Text>
        <Text>
          <b>4. Program Description:</b> Without limiting the scope of the Program, users that opt
          into the Program can expect to receive messages concerning the marketing, promotion,
          payment, customer support, delivery (including order confirmations, tracking information,
          and shipping updated via email), other transactional-related messages and sale of goods
          and services. Messages may include checkout reminders.
        </Text>
        <Text>
          <b>5. AI-Generated Message Content:</b> You are advised that from time to time We may
          utilize certain generative artificial intelligence (AI) services provided by third-parties
          to develop or suggest the content of messages that we choose to send to you. This may
          include AI-generated voices. Some of these messages may be tailored to your interests
          based on information available to Us. By enrolling and remaining in the program, You agree
          to the use of AI and further that Our use of these AI services does not make those
          third-parties either senders or initiators of the messages or otherwise responsible for
          the messages.
        </Text>
        <Text>
          <b>6. Message Frequency, Cost and Changes.</b> Message and data rates may apply. You agree
          to receive messages periodically at Our discretion. Daily, weekly, and monthly message
          frequency will vary. We reserve the right to alter the frequency of messages sent at any
          time, so as to increase or decrease the total number of sent messages. We also reserve the
          right to change the short code or phone number from which Our messages are sent.
        </Text>
        <Text>
          <b>7. Support Instructions.</b> For support regarding the Program, text "HELP" to the
          number you received messages from or email us at{' '}
          <ALink href="mailto:help@myskylight.com">help@myskylight.com</ALink>. Please note that the
          use of this email address is not an acceptable method of opting out of the program. Opt
          outs must be submitted in accordance with the procedures set forth above.
        </Text>
        <Text>
          <b>8. RCS and MMS Disclosure.</b> The Program may send SMS or MMS messages if your mobile
          device does not support RCS. The Program will send SMS messages if your mobile device does
          not support MMS messaging.
        </Text>
        <Text>
          <b>9. Our Disclaimer of Warranty.</b> The Program is offered on an "as-is" basis and may
          not be available in all areas at all times and may not continue to work in the event of
          product, software, coverage or other changes made by your wireless carrier. We will not be
          liable for any delays or failures in the receipt of any mobile messages connected with
          this Program. Delivery of mobile messages is subject to effective transmission from your
          wireless service provider/network operator and is outside of Our control. Carriers are not
          liable for delayed or undelivered mobile messages.
        </Text>
        <Text>
          <b>10. Supported Devices.</b> The Program is offered on an "as-is" basis. Not all mobile
          devices or handsets may be supported and our messages may not be deliverable in all areas.
          Skylight Frame, its service providers and the mobile carriers supported by the program are
          not liable for delayed or undelivered messages.
        </Text>
        <Text>
          <b>11. Contact.</b> This Program is a service of Skylight Frame, located at 101A Clay
          Street 144, San Francisco CA 94111, US.
        </Text>
        <Text>
          <b>12. Age Restriction:</b> You may not use or engage with the Platform if you are under
          thirteen (13) years of age. If you use or engage with the Platform and are between the
          ages of thirteen (13) and eighteen (18) years of age, you must have your parent's or legal
          guardian's permission to do so. By using or engaging with the Platform, you acknowledge
          and agree that you are not under the age of thirteen (13) years, are between the ages of
          thirteen (13) and eighteen (18) and have your parent's or legal guardian's permission to
          use or engage with the Platform, or are of adult age in your jurisdiction. By using or
          engaging with the Platform, you also acknowledge and agree that you are permitted by your
          jurisdiction's Applicable Law to use and/or engage with the Platform.
        </Text>
        <Text>
          <b>13. Prohibited Content:</b> You acknowledge and agree to not send any prohibited
          content over the Platform. Prohibited content includes:
        </Text>
        <PrivacyUnorderedList>
          <SimpleListItem>
            Any fraudulent, libelous, defamatory, scandalous, threatening, harassing, or stalking
            activity;
          </SimpleListItem>
          <SimpleListItem>
            Objectionable content, including profanity, obscenity, lasciviousness, violence,
            bigotry, hatred, and discrimination on the basis of race, sex, religion, nationality,
            disability, sexual orientation, or age;
          </SimpleListItem>
          <SimpleListItem>
            Pirated computer programs, viruses, worms, Trojan horses, or other harmful code;
          </SimpleListItem>
          <SimpleListItem>
            Any product, service, or promotion that is unlawful where such product, service, or
            promotion thereof is received;
          </SimpleListItem>
          <SimpleListItem>
            Any content that implicates and/or references personal health information that is
            protected by the Health Insurance Portability and Accountability Act ("HIPAA") or the
            Health Information Technology for Economic and Clinical Health Act ("HITEC" Act); and
          </SimpleListItem>
          <SimpleListItem>
            Any other content that is prohibited by Applicable Law in the jurisdiction from which
            the message is sent.
          </SimpleListItem>
        </PrivacyUnorderedList>
        <Text>
          <b>14. State Telemarketing Law - Residence:</b> To the extent the law is relevant and
          applicable to the Program, we endeavor to comply with applicable state telemarketing laws,
          including, but not limited to, the Florida Telemarketing Act and Florida Do Not Call Act,
          the Oklahoma Telephone Solicitation Act of 2022, the Maryland Stop the Spam Calls Act of
          2023, New Jersey's Senate Bill 921 (2023), and the Virginia Telephone Privacy Protection
          Act. For purposes of compliance with federal and state laws, you agree that we may assume
          that you are a resident of a particular state if, at the time of opt-in to the Program,
          the area code for the phone number used to opt-in to the Program is an area code
          associated with that state. Further, You agree that You will not assert that you are a
          resident of a state other than the state applicable to your area code unless you
          affirmatively advise us in writing that you are a resident of a specific state by sending
          written notice to us. You further agree that any mobile messages sent by Us in direct
          response to mobile messages or requests from You (including but are not limited to
          response to Keywords, opt-in, help or stop requests and shipping notifications) shall not
          constitute a "commercial telephone solicitation phone call", "telephonic sales call",
          "telemarketing sales call", "telephone solicitation", or "unsolicited telemarketing sales
          call" for purposes of federal and state laws, to the extent the law is otherwise relevant
          and applicable.
        </Text>
        <Text>
          <b>15. Subscribers Located in Texas:</b> Insofar as Texas law applies to your receipt of
          one or more text messages, and for purposes of applying Texas Business & Commerce Code
          Sections 302.058 and 302.059, the parties agree to the following construction. First, the
          word "customer" shall be interpreted in a manner consistent with the broadest dictionary
          definition and common usage. Customer shall not be interpreted to require the purchase of
          a product or service. Second, the word "establishment" shall be interpreted in a manner
          consistent with the broadest dictionary definition and common usage. Establishment shall
          not be interpreted to require the operation of a physical store, but rather shall include
          establishments that sell goods and services through an online store.
        </Text>
        <Text>
          <b>16. Dispute Resolution (Including Arbitration Agreement, Class Action Waiver).</b>
        </Text>
        <Text>
          Our Customer Service Specialists are ready to assist you and address your concerns—email
          us at: <ALink href="mailto:help@myskylight.com">help@myskylight.com</ALink>.
        </Text>
        <Text>
          THIS SECTION LIMITS CERTAIN RIGHTS, INCLUDING THE RIGHT TO MAINTAIN A COURT ACTION, THE
          RIGHT TO A JURY TRIAL, THE RIGHT TO PARTICIPATE IN ANY FORM OF CLASS, COLLECTIVE, OR
          REPRESENTATIVE CLAIM OR ACTION IN ARBITRATION AND LITIGATION, AND THE RIGHT TO CERTAIN
          REMEDIES AND FORMS OF RELIEF. OTHER RIGHTS THAT YOU OR Skylight Frame WOULD HAVE IN COURT,
          SUCH AS APPELLATE REVIEW, ALSO MAY NOT BE AVAILABLE IN ARBITRATION.
        </Text>
        <Text>
          <b>Binding Individual Arbitration:</b> n the event that there is a dispute, claim, or
          controversy between you and Us, or between you and Stodge Inc. d/b/a Postscript or any
          other third-party service provider acting on Our behalf to transmit the mobile messages
          within the scope of the Program, arising out of or relating to federal or state statutory
          claims, common law claims, this Agreement, or the breach, termination, enforcement,
          interpretation or validity thereof, including the determination of the scope or
          applicability of this agreement to arbitrate ("Dispute"), such Dispute will be, to the
          fullest extent permitted by law and applicable rules, determined by arbitration before one
          arbitrator, provided, however, that no party shall be precluded from seeking remedies in
          small claims court for disputes or claims within the scope of its jurisdiction. Whether a
          Dispute falls within the jurisdictional limits of small claims court is for the small
          claims court to decide. This clause shall not preclude parties from seeking provisional
          remedies in aid of arbitration from a court of appropriate jurisdiction.
        </Text>
        <Text>
          Dispute will be given the broadest possible meaning permitted by law. It includes, but is
          not limited to: (a) any dispute or claim that arose before the existence of these or any
          prior Terms and Conditions (including, but not limited to, claims relating to
          advertising); (b) any dispute or claim that is currently the subject of a purported class
          action litigation in which you are not a member of a certified class; and (c) any dispute
          or claim that may arise after termination of these Terms and Conditions and our
          relationship with you and Stodge Inc. d/b/a Postscript or any other third-party service
          provider acting on Our behalf. Dispute, however, does not include disagreements or claims
          concerning patents, copyrights, trademarks, trade secrets, or other intellectual property,
          and claims of piracy or unauthorized use of intellectual property. The arbitrator shall
          decide all issues that relate to the scope, validity, and enforceability of the Agreement.
          You and Skylight Frame agree that these Terms and Conditions evidence a transaction in
          interstate commerce and that this arbitration agreement will be interpreted and enforced
          in accordance with the Federal Arbitration Act and U.S. federal arbitration law and not
          state arbitration law.
        </Text>
        <Text>
          <b>Mandatory Informal Dispute Resolution Process.</b> If you and Skylight Frame have a
          Dispute, you and Skylight Frame agree to make a good faith effort to informally resolve
          it. The party initiating the Dispute must send a written notice to the other party that
          describes the Dispute. The notice must include all of this information: (a) the initiating
          party's contact information (including name, address, telephone number, and email address)
          (with their counsel's contact information, if represented); (b) sufficient information to
          enable the other party to identify any phone number(s), transaction(s), or account(s) at
          issue; and (b) a detailed description of (1) the Dispute, (2) the nature and basis of the
          claims, and (3) the nature and basis of the relief sought, with a detailed calculation for
          such relief. The notice must be personally signed by the party initiating the Dispute (and
          their counsel, if represented).
        </Text>
        <Text>
          If you have a Dispute with us, you must send this notice, including all of the information
          referenced above, by email to:{' '}
          <ALink href="mailto:help@myskylight.com">help@myskylight.com</ALink> or by mail to: 101A
          Clay Street 144, San Francisco CA 94111, US. If we have a Dispute with you, we will send
          this notice, including all of the information referenced above, to you at the most recent
          contact information we have on file for you or, if we do not have a mailing address on
          file, you authorize us to text you at the phone number we have available to seek your
          mailing address.
        </Text>
        <Text>
          For a period of sixty (60) days from receipt of a completed notice (which can be extended
          by agreement of the parties), you and we (and counsel, if you and we are represented)
          agree to negotiate in good faith in an effort to informally resolve the Dispute. To this
          end, the party receiving the notice may request a telephone or video settlement conference
          to aid in the resolution of the Dispute. If such a conference is requested, you and a
          Skylight Frame representative will personally attend (with counsel, if you and we are
          represented). The conference will be scheduled for a mutually convenient time, which may
          be outside of the 60-day period.
        </Text>
        <Text>
          If the Dispute is not resolved within sixty (60) days after receipt of a completed notice
          (which period can be extended by agreement of the parties), you or Skylight Frame may
          commence a formal dispute resolution proceeding consistent with the process set forth
          below.{' '}
          <b>
            Compliance with and completion of this Mandatory Informal Dispute Resolution Process
            ("Process") is a condition precedent to you or Skylight Frame commencing any formal
            dispute resolution proceeding in arbitration or small claims court. All applicable
            limitations periods (including statutes of limitations) will be tolled from the date of
            receipt of a completed notice through the conclusion of this Process.
          </b>{' '}
          If the sufficiency of a notice or compliance with this Process is at issue, such issue may
          be raised with and decided by a court at either party's election, and any formal dispute
          resolution proceeding shall be stayed pending resolution of the issue. A court of
          competent jurisdiction shall have the authority to enforce this condition precedent to
          arbitration, which includes the power to enjoin the filing or prosecution of a demand for
          arbitration. Nothing in this section limits the right of a party to raise the sufficiency
          of a notice or compliance with this Process or to seek damages for non-compliance with
          this Process in arbitration, including with a Process Arbitrator. You or we may commence
          arbitration only if the Dispute is not resolved through compliance with this Process.
        </Text>
        <Text>
          <b>Arbitration Generally; Relief Available.</b> There is no judge or jury in arbitration,
          and court review of an arbitration award is limited pursuant to the FAA. However, an
          arbitrator can award on an individual basis the same damages and relief as a court
          (including injunctive and declaratory relief or statutory damages), and must follow the
          Terms as a court would. For the avoidance of doubt, the arbitrator can award public
          injunctive relief if authorized by law and warranted by the individual claim(s).
        </Text>
        <Text>
          <b>
            Arbitration Proceedings and Rules, Including Provisions Governing Mass Arbitrations.
          </b>{' '}
          The following rules and procedures shall apply:
        </Text>
        <PrivacyUnorderedList>
          <SimpleListItem>
            Any arbitration will be administered by New Era ADR in accordance with their Virtual
            Expedited Arbitration Rules and Procedures, as well as any applicable General Rules and
            Procedures, except as modified by the Terms. New Era ADR's Virtual Expedited Arbitration
            Rules and Procedures and General Rules and Procedures are both available at{' '}
            <ALink
              href="https://www.neweraadr.com/rules-and-procedures/"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.neweraadr.com/rules-and-procedures/
            </ALink>
            . Please review these procedures carefully because they may impact how your complaint is
            handled and these procedures differ from those that would otherwise apply if you elected
            to pursue your claim individually in small claims court.
          </SimpleListItem>
          <SimpleListItem>
            When You initiate arbitration, the only fee You are required to pay is New Era's
            consumer filing fee, which is currently a maximum of $300 for a consumer. All other fees
            or expenses charged by New Era ADR will be paid by us (unless the arbitrator finds that
            either the substance of your claim or the relief sought is frivolous or brought for an
            improper purpose). You are responsible for fees and expenses owed to your legal counsel,
            if any, unless the arbitrator determines that an award of attorney's fees is warranted
            under applicable law.
          </SimpleListItem>
          <SimpleListItem>Mass Arbitration Provisions:</SimpleListItem>
          <ul>
            <SimpleListItem>
              New Era ADR's rules and procedures include unique procedures for "mass arbitrations,"
              which are situations in which five (5) or more cases are filed that arise out of
              common issues of law and fact and are brought by the same law firm or group of law
              firms. Mass arbitration procedures are designed to provide for a more cost-effective
              resolution of disputes. Those mass arbitration procedures call for a limited number of
              cases to be treated as "bellwether" cases to be resolved on the merits earlier than
              other cases. The outcome of bellwether cases may be treated as precedent in evaluating
              the remaining cases. You understand and agree that these Procedures for Mass
              Arbitrations will apply and that they are designed to (a) lead to the streamlined and
              cost-effective resolution of claims; (b) ensure that large volume filings do not
              impose unnecessary burdens or impediments to the resolution and cost-effective
              adjudication of similar claims; and (c) preserve the integrity of the arbitration
              process. You also understand and agree that by choosing to bring your Dispute as a
              part of a Mass Arbitration that the resolution of your Dispute might be delayed and
              ultimately proceed in court and not in arbitration. The parties agree that as part of
              these procedures, their counsel shall meet and confer in good faith in an effort to
              resolve the Disputes, streamline procedures, address the exchange of information,
              modify the number of Disputes to be adjudicated, and conserve the parties' and New Era
              ADR's resources.
            </SimpleListItem>
            <SimpleListItem>
              Only the presiding Neutral may make determinations of fact or reach conclusions of law
              with respect to a Mass Arbitration Proceeding. Ultimate authority to determine whether
              cases arise out of Common Issues of Law and Fact rests with the presiding Neutral. If
              the presiding Neutral determines that one or more cases assigned to a Mass Arbitration
              Proceeding do not arise out of Common Issues of Law and Fact, or otherwise do not meet
              the definition of a Mass Arbitration, each such case will be removed from the Mass
              Arbitration Proceedings. Each party will select one "Bellwether Case" from all the
              cases that were filed. Those Bellwether Cases will proceed individually, but in
              parallel to the extent possible. The Neutral will issue a reasoned Lead Decision in
              each Bellwether Case. Although a Neutral may consider the analysis and result reached
              in prior Lead Decisions in deciding disputes in the same Mass Arbitration proceeding
              the Neutral in all events must individually decide each claim asserted by each party
              in a Mass Arbitration proceeding, giving due consideration to the facts and arguments
              advanced by the Parties in each case.
            </SimpleListItem>
            <SimpleListItem>
              New Era ADR's rules and procedures provide parties to mass arbitrations with the right
              to obtain certain information relating to bellwether cases and decisions that may
              impact their cases. You are encouraged to review those rules closely to understand how
              your rights may be impacted.
            </SimpleListItem>
          </ul>
          <SimpleListItem>
            If any provision of this Agreement is deemed by an arbitrator not to comply with the
            Minimum Fairness Standards published by New Era ADR and available at{' '}
            <ALink href="https://www.neweraadr.com/" target="_blank" rel="noopener noreferrer">
              www.neweraadr.com/
            </ALink>{' '}
            then the Minimum Fairness Standards should be deemed to be incorporated by reference and
            any offending provision shall be deemed unenforceable.
          </SimpleListItem>
          <SimpleListItem>
            The arbitrator shall be selected pursuant to New Era ADR's standard rank and strike
            process, as set forth in New Era ADR's General Rules and Procedures.
          </SimpleListItem>
          <SimpleListItem>
            Except as otherwise provided herein, the arbitrator shall apply the substantive laws of
            the state and Federal Judicial Circuit in which Skylight Frame's principle place of
            business is located, without regard to its conflict of laws rules.
          </SimpleListItem>
          <SimpleListItem>
            By signing the demand for arbitration, a party (and their counsel, if represented)
            certifies that they have complied with (a) the Dispute Resolution provisions of this
            agreement and (b) all of the requirements of Federal Rule of Civil Procedure 11(b),
            including that the claims and relief sought are neither frivolous nor brought for an
            improper purpose. The arbitrator is authorized to award any relief or impose any
            sanctions available under Federal Rule of Civil Procedure 11 or applicable federal or
            state law against all represented parties and counsel as a court would.
          </SimpleListItem>
          <SimpleListItem>
            The arbitrator may award any relief or remedy that would be available in a court of law,
            including attorneys' fees and punitive damages where permitted by statute and is
            permitted to apply the cost-shifting provisions of Federal Rule of Civil Procedure 68
            after entry of an award.
          </SimpleListItem>
          <SimpleListItem>
            The arbitrator shall issue a reasoned written decision sufficient to explain the
            essential findings and conclusions. In the event of a Mass Arbitration, the parties
            agree that, for the sake of efficiency, an arbitrator's written decision may address
            some or all of the cases jointly. However, a written decision that jointly addresses
            some or all of the cases does not conflict with the requirement that the Neutral in all
            events must individually decide each claim asserted by each party in a Mass Arbitration
            proceeding.
          </SimpleListItem>
          <SimpleListItem>
            Notwithstanding anything contained in the New Era ADR rules, the decision of the
            arbitrator shall be final and binding on both parties, but either party shall have
            rights of appeal expressly provided in section 10 of the FAA.
          </SimpleListItem>
          <SimpleListItem>
            Except as may be required by law, neither a party nor the arbitrator may disclose the
            existence, content, or results of any arbitration without the prior written consent of
            both parties, unless to protect or pursue a legal right. Notwithstanding anything to the
            contrary in this Agreement, if Skylight Frame makes any future change to this
            arbitration provision, you may reject the change by sending Us written notice within 30
            days of the change to 101A Clay Street 144, San Francisco CA 94111, US, in which case
            this arbitration provision, as in effect immediately prior to the changes you rejected,
            will continue to govern any disputes between you and Skylight Frame.
          </SimpleListItem>
        </PrivacyUnorderedList>
        <Text>
          <b>No class actions or juries.</b> YOU UNDERSTAND AND AGREE THAT, BY AGREEING TO THIS
          AGREEMENT, YOU AND Skylight Frame ARE EACH WAIVING THE RIGHT TO A TRIAL BY JURY OR TO
          PARTICIPATE IN A CLASS ACTION IN COURT AND THAT THIS AGREEMENT SHALL BE SUBJECT TO AND
          GOVERNED BY THE FEDERAL ARBITRATION ACT (the "FAA").{' '}
          <b>
            The parties agree to arbitrate solely on an individual basis, and this agreement does
            not permit class arbitration or any claims brought as a plaintiff or class member in any
            class or representative arbitration proceeding EXCEPT as provided in this Agreement.
          </b>
        </Text>
        <Text>
          <b>Invalidity and Survivability.</b> If any term or provision of this Section is invalid,
          illegal, or unenforceable in any jurisdiction, such invalidity, illegality, or
          unenforceability shall not affect any other term or provision of this Section or
          invalidate or render unenforceable such term or provision in any other jurisdiction. If
          for any reason a dispute proceeds in court rather than in arbitration, the parties hereby
          waive any right to a jury trial. This arbitration provision shall survive any cancellation
          or termination of your agreement to participate in any of our Programs.
        </Text>
        <Text>
          <b>Future Changes to Dispute Resolution Section.</b> Notwithstanding any provision to the
          contrary, you and we agree that if Skylight Frame makes any future changes to this
          arbitration agreement (other than a change to the mailing or email address), you may
          reject any such change by sending us written notice personally signed by you within thirty
          (30) days of the change to Skylight Frame at 101A Clay Street 144, San Francisco CA 94111,
          US. The written notice must include the following information: (a) your name, address,
          phone number, and email address and (b) a statement that you wish to opt out of changes to
          the arbitration agreement. Such written notice does not constitute an opt out of
          arbitration altogether. By rejecting any future change to the arbitration agreement, you
          are agreeing that you will arbitrate any Dispute between us in accordance with the
          language of this version of the arbitration agreement.
        </Text>
        <Text>
          <b>17. Miscellaneous.</b> You warrant and represent to Us that you have all necessary
          rights, power, and authority to agree to the terms and perform your obligations hereunder,
          and nothing contained in this Agreement or in the performance of such obligations will
          place you in breach of any other contract or obligation. The failure of either party to
          exercise in any respect any right provided for herein will not be deemed a waiver of any
          further rights hereunder. If any provision of this Agreement is found to be unenforceable
          or invalid, that provision will be limited or eliminated to the minimum extent necessary
          so that the terms of this Agreement will otherwise remain in full force and effect and
          enforceable. Any new features, changes, updates or improvements of the Program shall be
          subject to these terms unless explicitly stated otherwise in writing. We reserve the right
          to change these terms from time to time. Any updates to this Agreement shall be
          communicated to you. Prior to such communication, the terms of this Agreement in effect as
          last made available to you immediately prior to your receipt of the notice will continue
          to govern our relationship. You acknowledge your responsibility to review this Agreement
          from time to time and to be aware of any such changes. By continuing to participate in the
          Program after any such changes, you accept this Agreement, as modified.
        </Text>
        <Text style={{ textAlign: 'center' }}>
          <b>Appendix A</b>
        </Text>
        <Text style={{ textAlign: 'center' }}>
          <b>Skylight Frame</b>
          <br />
          <b>Do Not Call Policy: Text Messaging</b>
          <br />
        </Text>
        <Text>
          <b>Regulatory Summary Regarding The Telephone Consumer Protection Act (TCPA)</b>
        </Text>
        <Text>
          The federal Telephone Consumer Protection Act (TCPA), related FCC regulations, and related
          court interpretations protect consumers from specific types of telemarketing. Under the
          TCPA "telemarketing" is defined as "the initiation of a telephone call or message for the
          purpose of encouraging the purchase or rental of, or investment in, property, goods, or
          services, which is transmitted to any person." 47 C.F.R. § 64.1200(f)(13). The TCPA
          imposes requirements for cold calls, prerecorded sales calls, and the use of autodialers.
          The TCPA also authorized the creation of the National Do Not Call Registry as well as
          internal Do Not Call lists. Various states have also adopted their own telemarketing laws.
        </Text>
        <Text>
          Skylight Frame is committed to complying with federal and state Do Not Call laws. This
          policy relates specifically to our compliance with those requirements for the purposes of
          sending SMS and MMS messages that constitute telemarketing. It is Our policy to not send
          telemarketing SMS and MMS messages to:
        </Text>
        <PrivacyUnorderedList>
          <SimpleListItem>
            Any telephone number, using an automatic telephone dialing system, for which we have not
            received prior express written consent, as defined by the Federal Communications
            Commission; and
          </SimpleListItem>
          <SimpleListItem>Any telephone number on Our Internal Do Not Call List.</SimpleListItem>
        </PrivacyUnorderedList>
        <Text>
          We will maintain an Internal Do Not Call list and will promptly honor a request made in
          one of the following manners to place your telephone number Our Do Not Call list within a
          reasonable time of such request, not to exceed 10 business days from the date of said
          request. To be placed on Our Do Not Call list, you may:
        </Text>
        <PrivacyUnorderedList>
          <SimpleListItem>
            Reply STOP, END, CANCEL, REVOKE, OPT OUT, UNSUBSCRIBE, or QUIT to any mobile message
            from Us in order to opt out of the Program. Any other language used may not be
            recognized by Our system which may result in the request to opt-out not being processed.
          </SimpleListItem>
          <SimpleListItem>
            Request to be put on Our list by contacting customer service at{' '}
            <ALink href="mailto:help@myskylight.com">help@myskylight.com</ALink> and providing your
            name and telephone number.
          </SimpleListItem>
        </PrivacyUnorderedList>
        <Text>
          Your telephone number shall be retained indefinitely on Our Do Not Call list unless you
          subsequently provide new prior express written consent to rejoin our SMS list. If you
          change your number, you must request for the new number to be put on Our Do Not Call list.
        </Text>
      </OverflowContainer>
    </Container>
  </>
)

export default withTranslation()(SmsTerms)

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(filter: { ns: { in: ["common", "tos"] }, language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`
